import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI, aiAPI } from '../api';
import { getTranslation, LANGUAGE_OPTIONS } from '../i18n';
import LanguageTabs from '../components/LanguageTabs';
import TranslateFromEnButton from '../components/TranslateFromEnButton';
import PlatformFooter from '../components/PlatformFooter';
import { TEXT_LANGS, DEFAULT_TEXT_FIELDS, normalizeByLang } from '../utils/clientTexts';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const CANONICAL_SECTION_KEYS = ['gallery', 'client_uploads', 'notes', 'documents', 'final_decision'];
const SECTION_LABELS = {
  gallery: { title: 'Gallery', desc: 'The main moodboard images.' },
  client_uploads: { title: 'Client Uploads', desc: "Where clients add their own inspirations." },
  notes: { title: 'Notes', desc: 'Shared notes & comments between studio and client.' },
  documents: { title: 'Documents', desc: 'Briefs, offers, references attached to the board.' },
  final_decision: { title: 'Final Decision', desc: 'Star + lock images as the final pick.' },
};

const normalizeSectionOrder = (raw) => {
  const seen = new Set();
  const result = [];
  if (Array.isArray(raw)) {
    for (const item of raw) {
      const key = typeof item === 'string' ? item : item?.key;
      if (CANONICAL_SECTION_KEYS.includes(key) && !seen.has(key)) {
        result.push({ key, visible: item?.visible !== false });
        seen.add(key);
      }
    }
  }
  for (const k of CANONICAL_SECTION_KEYS) {
    if (!seen.has(k)) result.push({ key: k, visible: true });
  }
  return result;
};

const SortableSectionRow = ({ id, label, desc, visible, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '12px 14px',
    background: isDragging ? 'var(--kahla-cream)' : 'var(--kahla-white)',
    border: '1px solid var(--kahla-border)',
    marginBottom: '6px',
    boxShadow: isDragging ? '0 6px 18px rgba(0,0,0,0.08)' : 'none',
    opacity: visible ? 1 : 0.55,
  };
  return (
    <div ref={setNodeRef} style={style} data-testid={`tpl-section-row-${id}`}>
      <span
        {...attributes}
        {...listeners}
        title="Drag to reorder"
        style={{
          cursor: 'grab',
          padding: '4px 6px',
          color: 'var(--kahla-text-light)',
          fontSize: '14px',
          lineHeight: 1,
          userSelect: 'none',
        }}
      >
        ⋮⋮
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--kahla-charcoal)' }}>{label}</div>
        <div style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginTop: '2px' }}>{desc}</div>
      </div>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--kahla-text-light)', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={visible}
          onChange={(e) => onToggle(e.target.checked)}
          data-testid={`tpl-section-visible-${id}`}
          style={{ accentColor: 'var(--kahla-charcoal)' }}
        />
        {visible ? 'Visible' : 'Hidden'}
      </label>
    </div>
  );
};

const DEFAULT_TEXTS = { ...DEFAULT_TEXT_FIELDS };

const buildEmptyTextsByLang = () => ({
  en: { ...DEFAULT_TEXTS },
  pl: { ...DEFAULT_TEXTS },
  de: { ...DEFAULT_TEXTS },
});

// Maps text keys to their i18n translation keys
const TEXT_TO_I18N = {
  welcome_title: 'welcome',
  welcome_subtitle: 'client_intro',
  welcome_description: 'client_hint',
  instruction_text: 'instruction_main',
  instruction_react: 'instruction_react',
  inspiration_title: 'your_inspirations',
  inspiration_description: 'inspiration_intro',
  notes_title: 'notes_title',
  notes_description: 'notes_subtitle',
  documents_title: 'documents_title',
  documents_description: 'documents_subtitle',
};

// Section groupings for reset buttons
const SECTION_KEYS = {
  welcome: ['welcome_title', 'welcome_subtitle', 'welcome_description'],
  instruction: ['instruction_text', 'instruction_react'],
  inspiration: ['inspiration_title', 'inspiration_description'],
  notes: ['notes_title', 'notes_description'],
  documents: ['documents_title', 'documents_description'],
};

const TemplateEditor = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  document.title = 'Maestra - Templates';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [mode, setMode] = useState('edit'); // 'edit' | 'preview'

  // Template data
  const [name, setName] = useState('');
  const [labels, setLabels] = useState('');
  const [clientLang, setClientLang] = useState('en');       // default client language for boards made from this template
  const [editLang, setEditLang] = useState('en');           // which language tab we're editing
  // Preview always renders every section since templates no longer
  // gate feature visibility — that lives with Plans now. The preview is
  // a structural mock; live boards still respect the user's plan and
  // section_order at render time.
  const allowFinal = true;
  const allowNotes = true;
  const allowUploads = true;
  const allowDocs = true;
  const [reactionMode, setReactionMode] = useState('mood');
  const [isGlobal, setIsGlobal] = useState(false);
  const [textsByLang, setTextsByLang] = useState(buildEmptyTextsByLang());
  // Branding
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceLogoUrl, setWorkspaceLogoUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('');
  const [allowLogoUpload, setAllowLogoUpload] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [sectionOrder, setSectionOrder] = useState(normalizeSectionOrder(null));
  // Etap 6 — starter Gallery Sections that get copied (with fresh
  // ids) onto every new board created from this template. Managed as
  // a simple list of {title, description} rows; admin adds/removes/
  // reorders inline in the "Gallery Sections defaults" panel below.
  const [defaultGallerySections, setDefaultGallerySections] = useState([]);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const { data } = await adminAPI.getTemplate(templateId);
        setName(data.name || '');
        setLabels((data.labels || []).join(', '));
        setClientLang(data.client_language || 'en');
        setEditLang(data.client_language || 'en');
        const rm = (data.reaction_mode || 'mood').toString().toLowerCase();
        setReactionMode(['mood', 'simple', 'decision'].includes(rm) ? rm : 'mood');
        setIsGlobal(data.is_global === true);
        // Normalize backend texts (may be legacy flat or per-lang). Use the
        // raw saved values so admins see exactly what's persisted in the DB —
        // empty fields stay empty (no i18n pre-fill), which is what users will
        // see as the "Default: …" reference in their own settings.
        const normalized = normalizeByLang(data.texts);
        const seeded = {};
        for (const lang of TEXT_LANGS) {
          seeded[lang] = { ...DEFAULT_TEXTS, ...(normalized[lang] || {}) };
        }
        setTextsByLang(seeded);
        setWorkspaceName(data.workspace_name || '');
        setWorkspaceLogoUrl(data.workspace_logo_url || '');
        setPrimaryColor(data.primary_color || '');
        setAllowLogoUpload(data.allow_logo_upload || false);
        setSectionOrder(normalizeSectionOrder(data.section_order));
        setDefaultGallerySections(Array.isArray(data.default_gallery_sections) ? data.default_gallery_sections : []);
      } catch (err) {
        showToast('Failed to load template');
      } finally {
        setLoading(false);
      }
    };
    fetchTemplate();
  }, [templateId]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  // Current language tab's texts (used by all form fields)
  const texts = textsByLang[editLang] || { ...DEFAULT_TEXTS };

  const updateText = (key, value) => {
    setTextsByLang(prev => ({
      ...prev,
      [editLang]: { ...(prev[editLang] || {}), [key]: value },
    }));
  };

  // Reset a section's fields to EMPTY for the currently-edited language only
  // (empty = "no template default"; users on this template will see no
  // "Default: …" hint for these fields and the client view will fall back to
  // the static i18n bundle).
  const resetSection = (sectionName) => {
    const keys = SECTION_KEYS[sectionName];
    if (!keys) return;
    setTextsByLang(prev => {
      const updated = { ...(prev[editLang] || {}) };
      keys.forEach(key => {
        updated[key] = '';
      });
      return { ...prev, [editLang]: updated };
    });
    showToast(`Cleared ${editLang.toUpperCase()} ${sectionName}`);
  };

  // Section is "modified" when any of its fields has a non-empty value for the
  // currently-edited language — i.e. the admin has set an explicit default.
  const isSectionModified = (sectionName) => {
    const keys = SECTION_KEYS[sectionName];
    if (!keys) return false;
    return keys.some(key => (texts[key] || '').trim() !== '');
  };

  // When DEFAULT language changes, re-fill empty fields in that language only
  const handleLangChange = (newLang) => {
    setClientLang(newLang);
    setEditLang(newLang);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showToast('Template name is required');
      return;
    }
    setSaving(true);
    try {
      // ---- Auto-translate empty target language buckets from EN ----
      // For PL and DE: when the admin has typed an EN value but the same key
      // is empty in the target language, run AI translation so every studio
      // downstream lands in all 3 languages without admins having to think
      // about it. Best-effort — failures are silent and we still save.
      const enBucket = textsByLang.en || {};
      const autoTranslated = {};
      for (const lang of TEXT_LANGS) {
        if (lang === 'en') continue;
        const targetBucket = textsByLang[lang] || {};
        const missing = {};
        for (const k of Object.keys(TEXT_TO_I18N)) {
          const enVal = (enBucket[k] || '').trim();
          if (!enVal) continue;
          if ((targetBucket[k] || '').trim()) continue;
          missing[k] = enVal;
        }
        if (Object.keys(missing).length === 0) continue;
        try {
          showToast(`Translating to ${lang.toUpperCase()}…`);
          const { data } = await aiAPI.translateTexts('en', lang, missing);
          const translations = data?.translations || {};
          if (Object.keys(translations).length > 0) {
            autoTranslated[lang] = translations;
          }
        } catch (err) {
          // Silent — keep saving even if AI translation fails.
          // eslint-disable-next-line no-console
          console.warn(`Auto-translate to ${lang} failed`, err);
        }
      }

      // Reflect AI-filled buckets back into form state so admin sees them.
      if (Object.keys(autoTranslated).length > 0) {
        setTextsByLang(prev => {
          const next = { ...prev };
          for (const [lang, vals] of Object.entries(autoTranslated)) {
            next[lang] = { ...(prev[lang] || {}), ...vals };
          }
          return next;
        });
      }

      // Persist the admin's explicit per-language texts as-is. No i18n
      // round-trip stripping — what the admin types is what users see as the
      // "Default: …" reference in their own settings.
      const cleanByLang = {};
      for (const lang of TEXT_LANGS) {
        const src = { ...(textsByLang[lang] || {}), ...(autoTranslated[lang] || {}) };
        const cleaned = {};
        for (const key of Object.keys(TEXT_TO_I18N)) {
          cleaned[key] = (src[key] || '').trim();
        }
        cleanByLang[lang] = cleaned;
      }
      await adminAPI.updateTemplate(templateId, {
        name,
        labels: labels.split(',').map(l => l.trim()).filter(Boolean),
        client_language: clientLang,
        reaction_mode: reactionMode,
        texts: cleanByLang,
        workspace_name: workspaceName || null,
        workspace_logo_url: workspaceLogoUrl || null,
        primary_color: primaryColor || null,
        allow_logo_upload: allowLogoUpload,
        section_order: sectionOrder,
        default_gallery_sections: defaultGallerySections
          .map((s) => ({ title: (s.title || '').trim(), description: (s.description || '').trim() }))
          .filter((s) => s.title),
      });
      const filledLangs = Object.keys(autoTranslated);
      if (filledLangs.length > 0) {
        showToast(`Template saved · auto-filled ${filledLangs.map(l => l.toUpperCase()).join(' & ')}`);
      } else {
        showToast('Template saved');
      }
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard fade-in" style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  // Get i18n defaults for the currently-edited language (for preview mode)
  const t = getTranslation(editLang);
  const txt = {
    welcome: texts.welcome_title || t.welcome,
    client_intro: texts.welcome_subtitle || t.client_intro,
    client_hint: texts.welcome_description || t.client_hint,
    instruction_main: texts.instruction_text || t.instruction_main,
    instruction_react: texts.instruction_react || t.instruction_react,
    your_inspirations: texts.inspiration_title || t.your_inspirations,
    inspiration_intro: texts.inspiration_description || t.inspiration_intro,
    notes_title: texts.notes_title || t.notes_title,
    notes_subtitle: texts.notes_description || t.notes_subtitle,
    documents_title: texts.documents_title || t.documents_title,
    documents_subtitle: texts.documents_description || t.documents_subtitle,
  };

  return (
    <div className="dashboard fade-in" style={{ maxWidth: mode === 'preview' ? '100%' : '760px', margin: '0 auto', padding: mode === 'preview' ? '0' : '40px 24px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: mode === 'preview' ? '0' : '40px',
        padding: mode === 'preview' ? '16px 24px' : '0',
        background: mode === 'preview' ? 'var(--kahla-white)' : 'transparent',
        borderBottom: mode === 'preview' ? '1px solid var(--kahla-border)' : 'none',
        position: mode === 'preview' ? 'sticky' : 'static',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            data-testid="back-to-admin"
            onClick={() => navigate('/admin')}
            style={{ background: 'none', border: 'none', fontSize: '12px', color: 'var(--kahla-text-light)', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}
          >
            &larr; Back
          </button>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', fontWeight: '600', margin: 0 }}>
            {name || 'Template'}
          </h1>
          {isGlobal && (
            <span style={{ fontSize: '9px', padding: '2px 6px', background: '#f0e6d3', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Global</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Mode toggle */}
          <div style={{
            display: 'flex',
            border: '1px solid var(--kahla-border)',
            overflow: 'hidden',
          }}>
            <button
              data-testid="mode-edit"
              onClick={() => setMode('edit')}
              style={{
                padding: '8px 16px',
                fontSize: '12px',
                letterSpacing: '0.04em',
                background: mode === 'edit' ? 'var(--kahla-text)' : 'transparent',
                color: mode === 'edit' ? 'var(--kahla-white, #fff)' : 'var(--kahla-text)',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Edit
            </button>
            <button
              data-testid="mode-preview"
              onClick={() => setMode('preview')}
              style={{
                padding: '8px 16px',
                fontSize: '12px',
                letterSpacing: '0.04em',
                background: mode === 'preview' ? 'var(--kahla-text)' : 'transparent',
                color: mode === 'preview' ? 'var(--kahla-white, #fff)' : 'var(--kahla-text)',
                border: 'none',
                borderLeft: '1px solid var(--kahla-border)',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Preview
            </button>
          </div>
          <button
            className="btn-primary"
            data-testid="save-template"
            onClick={handleSave}
            disabled={saving}
            style={{ minWidth: '100px' }}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* ==================== EDIT MODE ==================== */}
      {mode === 'edit' && (
        <div>
          <Section title="Basic Settings">
            <Field label="Template Name">
              <input data-testid="tpl-name" type="text" value={name} onChange={e => setName(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Default Labels (comma separated)">
              <input data-testid="tpl-labels" type="text" value={labels} onChange={e => setLabels(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Default Client Language">
              <select data-testid="tpl-lang" value={clientLang} onChange={e => handleLangChange(e.target.value)} style={inputStyle}>
                {LANGUAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          </Section>

          <Section title="Feature Access" subtitle="Feature access now lives with each user&rsquo;s Plan. Open Admin &rarr; Workspaces &rarr; Plans to manage what each plan unlocks, or tailor a single user via the Plan drawer in the Users tab.">
            <div style={{
              padding: '14px 16px',
              background: '#faf8f5',
              border: '1px dashed var(--kahla-border)',
              fontSize: '12px', color: 'var(--kahla-text-light)',
              fontStyle: 'italic', lineHeight: 1.6,
            }} data-testid="tpl-features-moved-notice">
              Templates carry workflow defaults only &mdash; labels, language, reaction mode, section order and texts. Feature toggles moved to Plans so a single template can serve workspaces on different tiers.
            </div>
          </Section>

          <Section title="Reaction Mode" subtitle="Choose the reaction vocabulary clients see when they tap an image. Internal data stays consistent across modes — only labels change.">
            <div
              role="radiogroup"
              data-testid="tpl-reaction-mode-group"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}
            >
              {[
                {
                  value: 'mood',
                  label: 'Mood Reactions',
                  preview: '❤️ I feel this · 👍 Maybe · ✕ Not this',
                  description: 'Default. Used for inspiration and discovery phases.',
                },
                {
                  value: 'simple',
                  label: 'Simple Reactions',
                  preview: 'Yes · Maybe · No',
                  description: 'Light approval workflow. Reads as a quiet preference.',
                },
                {
                  value: 'decision',
                  label: 'Decision Reactions',
                  preview: 'Agree · Not agree · Remove',
                  description: 'Practical approval workflow. Reads as a clear decision.',
                },
              ].map((opt) => {
                const selected = reactionMode === opt.value;
                return (
                  <label
                    key={opt.value}
                    data-testid={`tpl-reaction-mode-${opt.value}`}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      padding: '14px 16px',
                      border: `1px solid ${selected ? 'var(--kahla-charcoal)' : 'var(--kahla-border)'}`,
                      background: selected ? '#f8f6f3' : 'transparent',
                      cursor: 'pointer',
                      transition: 'background 0.15s, border-color 0.15s',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="radio"
                        name="reaction_mode"
                        value={opt.value}
                        checked={selected}
                        onChange={() => setReactionMode(opt.value)}
                        style={{ width: '14px', height: '14px', accentColor: '#8B7355' }}
                      />
                      <span style={{ fontSize: '13px', color: 'var(--kahla-charcoal)' }}>
                        {opt.label}
                      </span>
                    </span>
                    <span style={{
                      fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                      fontStyle: 'italic',
                      fontSize: '12px',
                      color: 'var(--kahla-text-light)',
                      lineHeight: 1.5,
                    }}>
                      {opt.description}
                    </span>
                    <span style={{
                      fontSize: '11px',
                      letterSpacing: '0.06em',
                      color: 'var(--kahla-taupe)',
                      marginTop: '2px',
                    }}>
                      {opt.preview}
                    </span>
                  </label>
                );
              })}
            </div>
          </Section>

          <Section
            title="Section Order"
            subtitle="Drag to reorder sections in the client view, and toggle visibility per section. Hidden sections will not appear on the board."
          >
            <DndContext
              sensors={dndSensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => {
                const { active, over } = event;
                if (!over || active.id === over.id) return;
                setSectionOrder((items) => {
                  const oldIndex = items.findIndex((i) => i.key === active.id);
                  const newIndex = items.findIndex((i) => i.key === over.id);
                  if (oldIndex < 0 || newIndex < 0) return items;
                  return arrayMove(items, oldIndex, newIndex);
                });
              }}
            >
              <SortableContext items={sectionOrder.map((s) => s.key)} strategy={verticalListSortingStrategy}>
                <div data-testid="tpl-section-order-list">
                  {sectionOrder.map((s) => (
                    <SortableSectionRow
                      key={s.key}
                      id={s.key}
                      label={SECTION_LABELS[s.key]?.title || s.key}
                      desc={SECTION_LABELS[s.key]?.desc || ''}
                      visible={s.visible}
                      onToggle={(v) =>
                        setSectionOrder((items) =>
                          items.map((it) => (it.key === s.key ? { ...it, visible: v } : it))
                        )
                      }
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            <button
              type="button"
              data-testid="tpl-section-order-reset"
              onClick={() => setSectionOrder(normalizeSectionOrder(null))}
              style={{
                marginTop: '8px',
                background: 'none',
                border: 'none',
                padding: '4px 0',
                fontSize: '11px',
                color: 'var(--kahla-taupe)',
                cursor: 'pointer',
                textDecoration: 'underline',
                textDecorationColor: 'rgba(166,152,136,0.35)',
                textUnderlineOffset: '3px',
                fontFamily: 'inherit',
              }}
            >
              Reset to default order
            </button>
          </Section>

          <Section
            title="Gallery Sections defaults"
            subtitle="Optional starter sections copied onto every new board created from this template. Boards can freely rename / delete / reorder after creation — templates are only the seed."
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }} data-testid="tpl-default-gallery-sections">
              {defaultGallerySections.length === 0 && (
                <p style={{
                  fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                  fontStyle: 'italic',
                  fontSize: '13px',
                  color: 'var(--kahla-text-light)',
                  margin: 0,
                }}>
                  No default sections. New boards start with a flat gallery.
                </p>
              )}
              {defaultGallerySections.map((s, idx) => (
                <div key={idx} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '8px',
                  padding: '10px 12px', background: 'var(--kahla-cream-light, #f7f3ea)',
                  border: '1px solid var(--kahla-border)',
                }}>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <input
                      type="text"
                      value={s.title || ''}
                      maxLength={80}
                      placeholder="e.g. Flowers"
                      data-testid={`tpl-default-section-title-${idx}`}
                      onChange={(e) => {
                        const next = [...defaultGallerySections];
                        next[idx] = { ...next[idx], title: e.target.value.slice(0, 80) };
                        setDefaultGallerySections(next);
                      }}
                      style={{ ...inputStyle, fontFamily: 'Playfair Display, serif', fontSize: '13px', letterSpacing: '0.14em', textTransform: 'uppercase' }}
                    />
                    <input
                      type="text"
                      value={s.description || ''}
                      maxLength={500}
                      placeholder="Optional short description"
                      data-testid={`tpl-default-section-desc-${idx}`}
                      onChange={(e) => {
                        const next = [...defaultGallerySections];
                        next[idx] = { ...next[idx], description: e.target.value.slice(0, 500) };
                        setDefaultGallerySections(next);
                      }}
                      style={{ ...inputStyle, fontStyle: 'italic', fontSize: '12px', color: 'var(--kahla-text-light)' }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <button
                      type="button"
                      disabled={idx === 0}
                      data-testid={`tpl-default-section-up-${idx}`}
                      onClick={() => {
                        if (idx === 0) return;
                        const next = [...defaultGallerySections];
                        [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                        setDefaultGallerySections(next);
                      }}
                      style={{ width: 24, height: 24, background: 'transparent', border: '1px solid var(--kahla-border)', cursor: idx === 0 ? 'not-allowed' : 'pointer', color: idx === 0 ? 'var(--kahla-border)' : 'var(--kahla-text)' }}
                    >↑</button>
                    <button
                      type="button"
                      disabled={idx === defaultGallerySections.length - 1}
                      data-testid={`tpl-default-section-down-${idx}`}
                      onClick={() => {
                        if (idx === defaultGallerySections.length - 1) return;
                        const next = [...defaultGallerySections];
                        [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
                        setDefaultGallerySections(next);
                      }}
                      style={{ width: 24, height: 24, background: 'transparent', border: '1px solid var(--kahla-border)', cursor: idx === defaultGallerySections.length - 1 ? 'not-allowed' : 'pointer', color: idx === defaultGallerySections.length - 1 ? 'var(--kahla-border)' : 'var(--kahla-text)' }}
                    >↓</button>
                  </div>
                  <button
                    type="button"
                    data-testid={`tpl-default-section-remove-${idx}`}
                    onClick={() => {
                      const next = defaultGallerySections.filter((_, i) => i !== idx);
                      setDefaultGallerySections(next);
                    }}
                    aria-label="Remove"
                    style={{
                      width: 28, height: 28, background: 'transparent',
                      border: '1px solid var(--kahla-border)', cursor: 'pointer',
                      color: 'var(--kahla-text-light)', fontSize: 14,
                    }}
                  >×</button>
                </div>
              ))}
            </div>
            <button
              type="button"
              data-testid="tpl-default-section-add"
              onClick={() => setDefaultGallerySections([...defaultGallerySections, { title: '', description: '' }])}
              style={{
                marginTop: '12px',
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '8px 16px',
                background: 'transparent', border: '1px dashed var(--kahla-taupe)',
                cursor: 'pointer', fontFamily: 'Playfair Display, serif',
                fontSize: '11px', letterSpacing: '0.16em', textTransform: 'uppercase',
                color: 'var(--kahla-charcoal)',
              }}
            >
              <span aria-hidden="true">+</span>
              <span>Add default section</span>
            </button>
          </Section>

          <Section title="Branding Settings" subtitle="Controls how the workspace and client view appear">
            <Field label="Brand name" hint="Shown in client view, branding header, and exports. Leave empty for 'Maestra'.">
              <input data-testid="tpl-workspace-name" type="text" value={workspaceName} onChange={e => setWorkspaceName(e.target.value)} placeholder="Maestra" style={inputStyle} />
            </Field>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', padding: '10px 12px', background: allowLogoUpload ? '#f0f8f0' : '#f9f8f6', border: '1px solid var(--kahla-border)' }}>
              <input type="checkbox" checked={allowLogoUpload} onChange={e => setAllowLogoUpload(e.target.checked)} data-testid="tpl-allow-logo" id="allow-logo" />
              <label htmlFor="allow-logo" style={{ fontSize: '13px', color: 'var(--kahla-charcoal)', cursor: 'pointer' }}>
                Enable logo upload for this workspace
              </label>
              <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)' }}>(admin-controlled)</span>
            </div>

            {allowLogoUpload && (
              <Field label="Workspace Logo" hint="Upload a logo (PNG, JPEG, WebP, SVG, max 2MB). Shown in client view header and PDF exports.">
                {workspaceLogoUrl ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/images/${workspaceLogoUrl}`}
                      alt="Logo"
                      style={{ maxHeight: '48px', maxWidth: '160px', objectFit: 'contain', border: '1px solid var(--kahla-border)', padding: '4px' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <button
                      onClick={async () => {
                        try {
                          await adminAPI.deleteLogo(templateId);
                          setWorkspaceLogoUrl('');
                        } catch {}
                      }}
                      style={{ background: 'none', border: '1px solid var(--kahla-border)', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', color: '#c44' }}
                      data-testid="tpl-remove-logo"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <label style={{
                    display: 'inline-block', padding: '10px 20px',
                    border: '2px dashed var(--kahla-border)', cursor: 'pointer',
                    fontSize: '13px', color: 'var(--kahla-text-light)', textAlign: 'center',
                  }}>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      style={{ display: 'none' }}
                      data-testid="tpl-logo-upload"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setUploadingLogo(true);
                        try {
                          const { data } = await adminAPI.uploadLogo(templateId, file);
                          setWorkspaceLogoUrl(data.logo_url);
                        } catch (err) {
                          alert(err?.response?.data?.detail || 'Upload failed');
                        } finally {
                          setUploadingLogo(false);
                          e.target.value = '';
                        }
                      }}
                    />
                    {uploadingLogo ? 'Uploading...' : 'Choose logo file'}
                  </label>
                )}
              </Field>
            )}

            <Field label="Primary Color" hint="Optional. Hex color for accents (e.g. #3a3a3a).">
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input data-testid="tpl-primary-color" type="text" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} placeholder="#3a3a3a" style={{ ...inputStyle, flex: 1 }} />
                {primaryColor && (
                  <div style={{ width: '32px', height: '32px', background: primaryColor, border: '1px solid var(--kahla-border)', flexShrink: 0 }} />
                )}
              </div>
            </Field>
          </Section>

          {/* Client-view texts: edit each language separately via tabs */}
          <div
            style={{
              marginBottom: '16px',
              padding: '16px 20px',
              background: 'var(--kahla-cream-light)',
              border: '1px solid var(--kahla-border)',
            }}
          >
            <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>
              Client View Texts
            </p>
            <p style={{ fontSize: '12px', color: 'var(--kahla-text-light)', lineHeight: 1.5, marginBottom: '14px' }}>
              Edit client-facing texts in each language. When a board's client language is set, the client sees the matching tab's texts — with English as a silent fallback if a field is empty.
            </p>
            <LanguageTabs value={editLang} onChange={setEditLang} testId="tpl-lang-tabs" />
            <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', fontStyle: 'italic', marginBottom: '10px' }}>
              Currently editing: <strong style={{ color: 'var(--kahla-charcoal)' }}>{editLang.toUpperCase()}</strong>
            </p>
            <TranslateFromEnButton
              targetLang={editLang}
              testId="tpl-translate-btn"
              getSourceTexts={() => textsByLang.en}
              getTargetTexts={() => textsByLang[editLang] || {}}
              onTranslated={(translations) => {
                setTextsByLang(prev => ({
                  ...prev,
                  [editLang]: { ...(prev[editLang] || {}), ...translations },
                }));
              }}
            />
          </div>

          <Section title="Welcome Section" subtitle="Shown at the top of the client board" onReset={() => resetSection('welcome')} showReset={isSectionModified('welcome')}>
            <Field label="Welcome Title" hint="Greeting text shown prominently at the top." value={texts.welcome_title} emptyWarning>
              <input data-testid="tpl-welcome-title" type="text" value={texts.welcome_title} onChange={e => updateText('welcome_title', e.target.value)} placeholder="Enter welcome title..." style={inputStyle} />
            </Field>
            <Field label="Welcome Subtitle" hint="Main introduction paragraph" value={texts.welcome_subtitle} emptyWarning>
              <textarea data-testid="tpl-welcome-subtitle" value={texts.welcome_subtitle} onChange={e => updateText('welcome_subtitle', e.target.value)} placeholder="Enter introduction text..." style={{ ...inputStyle, minHeight: '72px', resize: 'vertical' }} />
            </Field>
            <Field label="Welcome Hint" hint="Italicized text below subtitle" value={texts.welcome_description} emptyWarning>
              <input data-testid="tpl-welcome-description" type="text" value={texts.welcome_description} onChange={e => updateText('welcome_description', e.target.value)} placeholder="Enter a subtle hint..." style={inputStyle} />
            </Field>
          </Section>

          <Section title="Instruction Box" subtitle="Displayed above images in the client view" onReset={() => resetSection('instruction')} showReset={isSectionModified('instruction')}>
            <Field label="Instruction Text" hint="Main instruction. Use \\n for line breaks." value={texts.instruction_text} emptyWarning>
              <textarea data-testid="tpl-instruction-text" value={texts.instruction_text} onChange={e => updateText('instruction_text', e.target.value)} placeholder="Enter main instruction..." style={{ ...inputStyle, minHeight: '72px', resize: 'vertical' }} />
            </Field>
            <Field label="Instruction React" hint="Italicized sub-instruction" value={texts.instruction_react} emptyWarning>
              <input data-testid="tpl-instruction-react" type="text" value={texts.instruction_react} onChange={e => updateText('instruction_react', e.target.value)} placeholder="Enter reaction instruction..." style={inputStyle} />
            </Field>
          </Section>

          <Section title="Client Inspirations Section" subtitle="Where clients upload their own images" onReset={() => resetSection('inspiration')} showReset={isSectionModified('inspiration')}>
            <Field label="Section Title" value={texts.inspiration_title} emptyWarning>
              <input data-testid="tpl-inspiration-title" type="text" value={texts.inspiration_title} onChange={e => updateText('inspiration_title', e.target.value)} placeholder="Enter section title..." style={inputStyle} />
            </Field>
            <Field label="Section Description" value={texts.inspiration_description} emptyWarning>
              <textarea data-testid="tpl-inspiration-desc" value={texts.inspiration_description} onChange={e => updateText('inspiration_description', e.target.value)} placeholder="Enter section description..." style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} />
            </Field>
          </Section>

          <Section title="Notes Section" subtitle="Shared notes between owner and client" onReset={() => resetSection('notes')} showReset={isSectionModified('notes')}>
            <Field label="Section Title" value={texts.notes_title} emptyWarning>
              <input data-testid="tpl-notes-title" type="text" value={texts.notes_title} onChange={e => updateText('notes_title', e.target.value)} placeholder="Enter section title..." style={inputStyle} />
            </Field>
            <Field label="Section Description" value={texts.notes_description} emptyWarning>
              <input data-testid="tpl-notes-desc" type="text" value={texts.notes_description} onChange={e => updateText('notes_description', e.target.value)} placeholder="Enter section description..." style={inputStyle} />
            </Field>
          </Section>

          <Section title="Documents Section" subtitle="Project materials for the client" onReset={() => resetSection('documents')} showReset={isSectionModified('documents')}>
            <Field label="Section Title" value={texts.documents_title} emptyWarning>
              <input data-testid="tpl-documents-title" type="text" value={texts.documents_title} onChange={e => updateText('documents_title', e.target.value)} placeholder="Enter section title..." style={inputStyle} />
            </Field>
            <Field label="Section Description" value={texts.documents_description} emptyWarning>
              <input data-testid="tpl-documents-desc" type="text" value={texts.documents_description} onChange={e => updateText('documents_description', e.target.value)} placeholder="Enter section description..." style={inputStyle} />
            </Field>
          </Section>
        </div>
      )}

      {/* ==================== PREVIEW MODE ==================== */}
      {mode === 'preview' && (
        <TemplatePreview
          txt={txt}
          t={t}
          allowNotes={allowNotes}
          allowUploads={allowUploads}
          allowDocs={allowDocs}
          allowFinal={allowFinal}
          labels={labels.split(',').map(l => l.trim()).filter(Boolean)}
          template={{ workspace_name: workspaceName, workspace_logo_url: workspaceLogoUrl, primary_color: primaryColor }}
        />
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
};

// ==================== TEMPLATE PREVIEW ====================

const MOCK_IMAGES = [
  { id: 'm1', label: null, color: '#d4c5b0' },
  { id: 'm2', label: null, color: '#c7b8a3' },
  { id: 'm3', label: null, color: '#baa896' },
  { id: 'm4', label: null, color: '#e0d5c8' },
  { id: 'm5', label: null, color: '#c2b5a5' },
  { id: 'm6', label: null, color: '#d8cfc3' },
];

const TemplatePreview = ({ txt, t, allowNotes, allowUploads, allowDocs, allowFinal, labels, template }) => {
  // Assign labels to mock images
  const images = MOCK_IMAGES.map((img, i) => ({
    ...img,
    label: labels[i % labels.length] || null,
  }));

  return (
    <div className="client-view fade-in" data-testid="template-preview" style={{ minHeight: 'auto' }}>
      {/* Preview badge */}
      <div style={{
        textAlign: 'center',
        padding: '8px',
        background: '#f0e6d3',
        fontSize: '11px',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: '#8B7355',
      }}>
        Template Preview — This is how clients will see the board
      </div>

      {/* Header */}
      <header className="client-header">
        <p className="client-logo">{template?.workspace_name || 'Maestra'}</p>
        <h1 className="client-board-name">{txt.welcome}</h1>
        <p style={{
          fontSize: '15px',
          color: 'var(--kahla-text-light)',
          maxWidth: '500px',
          margin: '16px auto 0',
          lineHeight: '1.7'
        }}>
          {txt.client_intro.split('\n').map((line, i) => <React.Fragment key={i}>{line}<br /></React.Fragment>)}
        </p>
        <p style={{
          fontSize: '13px',
          color: 'var(--kahla-text-light)',
          fontStyle: 'italic',
          marginTop: '24px',
          opacity: 0.8
        }}>
          {txt.client_hint}
        </p>
      </header>

      {/* Main Content */}
      <main className="client-content">
        {/* Instruction Box */}
        <div style={{
          maxWidth: '600px',
          margin: '0 auto 48px',
          textAlign: 'center',
          padding: '32px',
          backgroundColor: 'var(--kahla-cream-light)',
          border: '1px solid var(--kahla-border)'
        }}>
          <p style={{ fontSize: '14px', color: 'var(--kahla-text)', lineHeight: '1.8' }}>
            {txt.instruction_main.split('\n').map((line, i) => <React.Fragment key={i}>{line}<br /></React.Fragment>)}
          </p>
          <p style={{ fontSize: '14px', color: 'var(--kahla-text-light)', marginTop: '16px', fontStyle: 'italic' }}>
            {txt.instruction_react}
          </p>
        </div>

        {/* Final Decision Mode Banner */}
        {allowFinal && (
          <div style={{
            maxWidth: '500px',
            margin: '0 auto 40px',
            textAlign: 'center',
            padding: '20px 28px',
            border: '1px solid rgba(201,169,110,0.3)',
            backgroundColor: 'rgba(201,169,110,0.04)',
          }}>
            <p style={{ fontSize: '14px', color: '#C9A96E', fontStyle: 'italic', letterSpacing: '0.02em', lineHeight: '1.7' }}>
              {t.final_decision_prompt}
            </p>
          </div>
        )}

        {/* Mock Image Grid */}
        <div className="moodboard-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
          {images.map(img => (
            <div key={img.id} style={{
              position: 'relative',
              aspectRatio: '4/5',
              background: img.color,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              overflow: 'hidden',
            }}>
              {/* Mock image placeholder pattern */}
              <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.15,
              }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
              </div>
              {/* Section label */}
              {img.label && (
                <div style={{
                  padding: '8px 12px',
                  background: 'rgba(255,255,255,0.9)',
                  fontSize: '11px',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--kahla-text-light)',
                }}>
                  {img.label}
                </div>
              )}
              {/* Mock reaction bar */}
              <div style={{
                display: 'flex',
                gap: '8px',
                padding: '10px 12px',
                background: 'rgba(255,255,255,0.95)',
                justifyContent: 'center',
              }}>
                {['❤️', '👍', '❌'].map(r => (
                  <span key={r} style={{ fontSize: '16px', opacity: 0.4, cursor: 'default' }}>{r}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Client Inspirations */}
        {allowUploads && (
          <div className="inspiration-section">
            <h2 className="client-section-title">{txt.your_inspirations}</h2>
            <p style={{
              textAlign: 'center',
              fontSize: '14px',
              color: 'var(--kahla-text-light)',
              maxWidth: '500px',
              margin: '0 auto 16px',
              lineHeight: '1.7'
            }}>
              {txt.inspiration_intro.split('\n').map((line, i) => <React.Fragment key={i}>{line}<br /></React.Fragment>)}
            </p>
            {/* Mock upload area */}
            <div style={{
              maxWidth: '500px',
              margin: '0 auto',
              padding: '40px',
              border: '2px dashed var(--kahla-border)',
              textAlign: 'center',
              opacity: 0.6,
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ margin: '0 auto 12px', display: 'block', color: 'var(--kahla-text-light)' }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <p style={{ fontSize: '12px', color: 'var(--kahla-text-light)' }}>{t.upload_inspiration || 'Upload inspiration'}</p>
            </div>
          </div>
        )}

        {/* Notes Section */}
        {allowNotes && (
          <div style={{ maxWidth: '600px', margin: '48px auto', textAlign: 'center' }}>
            <h2 className="client-section-title">{txt.notes_title}</h2>
            <p style={{ textAlign: 'center', fontSize: '13px', color: 'var(--kahla-text-light)', marginBottom: '32px', fontStyle: 'italic' }}>
              {txt.notes_subtitle}
            </p>
            {/* Mock note input */}
            <div style={{
              padding: '20px',
              border: '1px solid var(--kahla-border)',
              background: 'var(--kahla-white)',
              opacity: 0.6,
            }}>
              <div style={{ width: '100%', padding: '12px', border: '1px solid var(--kahla-border)', background: '#faf8f5', fontSize: '13px', color: 'var(--kahla-text-light)', textAlign: 'left' }}>
                {t.notes_placeholder || 'Share your thoughts...'}
              </div>
            </div>
          </div>
        )}

        {/* Documents Section */}
        {allowDocs && (
          <div style={{ maxWidth: '600px', margin: '48px auto', textAlign: 'center' }}>
            <h2 className="client-section-title">{txt.documents_title}</h2>
            <p style={{ fontSize: '13px', color: 'var(--kahla-text-light)', marginBottom: '20px' }}>
              {txt.documents_subtitle}
            </p>
            {/* Mock document card */}
            <div style={{
              padding: '16px 20px',
              border: '1px solid var(--kahla-border)',
              background: 'var(--kahla-white)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              opacity: 0.6,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '32px', height: '32px', background: '#f0e6d3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#8B7355' }}>
                  PDF
                </div>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: '13px', fontWeight: '500' }}>Session Guide.pdf</div>
                  <div style={{ fontSize: '11px', color: 'var(--kahla-text-light)' }}>245 KB</div>
                </div>
              </div>
              <span style={{ fontSize: '12px', color: 'var(--kahla-text-light)' }}>View</span>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer style={{ textAlign: 'center', padding: '48px 0', borderTop: '1px solid var(--kahla-border)', marginTop: '48px' }}>
        <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '11px', letterSpacing: '0.15em', color: 'var(--kahla-text-light)' }}>
          {template?.workspace_name || 'Maestra'} {new Date().getFullYear()}
        </p>
        <div style={{ marginTop: 6, opacity: 0.7 }}>
          <PlatformFooter variant="compact" style={{ fontSize: 9, letterSpacing: '0.1em' }} />
        </div>
      </footer>
    </div>
  );
};

// ==================== UI Components ====================

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: '13px',
  border: '1px solid var(--kahla-border)',
  background: 'var(--kahla-white)',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  lineHeight: '1.5',
};

const Section = ({ title, subtitle, children, onReset, showReset }) => (
  <div style={{ marginBottom: '36px', paddingBottom: '36px', borderBottom: '1px solid var(--kahla-border)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: subtitle ? '4px' : '20px' }}>
      <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '16px', fontWeight: '600', color: 'var(--kahla-text)', margin: 0 }}>{title}</h2>
      {onReset && showReset && (
        <button
          data-testid={`reset-${title.toLowerCase().replace(/\s+/g, '-')}`}
          onClick={onReset}
          style={{
            background: 'none',
            border: '1px solid var(--kahla-border)',
            padding: '4px 10px',
            fontSize: '11px',
            color: 'var(--kahla-text-light)',
            cursor: 'pointer',
            fontFamily: 'inherit',
            letterSpacing: '0.03em',
            flexShrink: 0,
            transition: 'color 0.15s, border-color 0.15s',
          }}
          onMouseEnter={e => { e.target.style.color = 'var(--kahla-text)'; e.target.style.borderColor = 'var(--kahla-text)'; }}
          onMouseLeave={e => { e.target.style.color = 'var(--kahla-text-light)'; e.target.style.borderColor = 'var(--kahla-border)'; }}
        >
          Clear section
        </button>
      )}
    </div>
    {subtitle && <p style={{ fontSize: '12px', color: 'var(--kahla-text-light)', marginBottom: '20px' }}>{subtitle}</p>}
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>{children}</div>
  </div>
);

const Field = ({ label, hint, children, value, emptyWarning = false }) => {
  const showEmpty = emptyWarning && !((value || '').trim());
  return (
    <div>
      <label style={{ display: 'block', fontSize: '12px', color: 'var(--kahla-text)', marginBottom: '6px', fontWeight: '500' }}>{label}</label>
      {hint && <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginBottom: '6px', marginTop: '-2px' }}>{hint}</p>}
      {children}
      {showEmpty && (
        <p style={{
          margin: '6px 0 0',
          fontSize: '11px',
          fontStyle: 'italic',
          color: '#A8A29E',
          lineHeight: 1.45,
        }}>
          (empty — won't show as default to users)
        </p>
      )}
    </div>
  );
};

const Toggle = ({ label, description, checked, onChange, testId }) => (
  <label data-testid={testId} style={{
    display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px', color: 'var(--kahla-text)',
    cursor: 'pointer', padding: '12px 14px', border: '1px solid var(--kahla-border)',
    background: checked ? '#f8f6f3' : 'transparent', transition: 'background 0.15s',
  }}>
    <input
      type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
      style={{ width: '16px', height: '16px', accentColor: '#8B7355', marginTop: '2px', flexShrink: 0 }}
    />
    <span style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
      <span>{label}</span>
      {description && (
        <span style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif',
          fontStyle: 'italic',
          fontSize: '12px',
          color: 'var(--kahla-text-light)',
          lineHeight: 1.45,
        }}>
          {description}
        </span>
      )}
    </span>
  </label>
);

export default TemplateEditor;
